// Shared PII/secret scrubbing for Sentry events - the frontend counterpart
// to the backend's internal/redact package (see errtrack.go's scrubEvent).
// Roadmap #12 explicitly says not to build a second general-purpose
// redaction path, so this stays a small module scoped to what
// sentry.ts's beforeSend/beforeBreadcrumb hooks need, not a reusable
// redaction library.
//
// Why this matters here specifically: this app keeps a couple of real
// secrets in localStorage - the Cue signaling secret (`security.secret`)
// and per-provider AI API keys (`ai.key.<provider>`, `ai.deepseekApiKey` -
// see StoreContext.tsx's `LS` map). Nothing in the app captures
// localStorage into a Sentry event today, but if that's ever added (or a
// future Sentry integration starts doing it on its own), this scrubber is
// the safety net that keeps those values out of an outgoing event.

export const MASK = '***';

// Substrings (matched against a key with everything but letters/digits
// stripped and lowercased) that mark a field as sensitive. Deliberately
// broad, per the same "redact more rather than less" call the backend
// side makes - a false positive just hides an extra field from an error
// report, a false negative leaks a secret.
const SENSITIVE_KEY_SUBSTRINGS = [
  'password',
  'secret',
  'token',
  'apikey',
  'key',
  'auth',
  'cookie',
  'credential',
  'hash',
  'totp',
  'bearer',
  'basicauth',
];

function normalizeKey(key: string): string {
  return key.toLowerCase().replace(/[^a-z0-9]/g, '');
}

function isSensitiveKey(key: string): boolean {
  const normalized = normalizeKey(key);
  return SENSITIVE_KEY_SUBSTRINGS.some((s) => normalized.includes(s));
}

/**
 * Recursively masks sensitive object keys, mutating in place (and also
 * returning the same reference, for chaining). Safe against cycles and
 * non-object input.
 */
export function scrubValue<T>(value: T, seen: WeakSet<object> = new WeakSet()): T {
  if (value === null || typeof value !== 'object') return value;
  if (seen.has(value)) return value;
  seen.add(value);

  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i++) {
      value[i] = scrubValue(value[i], seen);
    }
    return value;
  }

  const obj = value as Record<string, unknown>;
  for (const k of Object.keys(obj)) {
    if (isSensitiveKey(k)) {
      obj[k] = MASK;
      continue;
    }
    obj[k] = scrubValue(obj[k], seen);
  }
  return value;
}

// Matches "<sensitive-word><rest-of-word>"?[:=]"?<value>"? in free text -
// covers both JSON-shaped substrings (`"apiKey":"sk-abc"`) and plain
// `key=value`/`key: value` formatting a stringified error might use. Only
// helps for text; scrubValue above handles structured data.
const TEXT_PATTERN = new RegExp(
  `\\b(${SENSITIVE_KEY_SUBSTRINGS.join('|')})[a-z0-9_]*"?\\s*[:=]\\s*"?([^"',}\\]\\s]+)"?`,
  'gi',
);

/** Masks key/value-shaped substrings inside a plain string (error messages,
 * stack traces, breadcrumb messages). */
export function scrubText(text: string | undefined): string | undefined {
  if (!text) return text;
  return text.replace(TEXT_PATTERN, (_match, key: string) => `${key}: ${MASK}`);
}
